import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '../ui/Badge';
import apiClient from '../../utils/api';

const CATEGORY_COLORS = {
  exercise: 'primary',
  nutrition: 'success',
  biometric: 'secondary',
  sleep: 'default',
  mental_health: 'warning',
  therapy: 'primary',
  medical: 'destructive',
  environmental: 'default',
  social: 'default',
  other: 'default',
};

const CATEGORY_LABELS = {
  'exercise.cardio': 'Cardio',
  'exercise.strength': 'Strength',
  'exercise.flexibility': 'Flexibility',
  'exercise': 'Exercise',
  'nutrition.food': 'Food',
  'nutrition.supplement': 'Supplement',
  'nutrition.hydration': 'Hydration',
  'nutrition': 'Nutrition',
  'biometric.vitals': 'Vitals',
  'biometric.body': 'Body',
  'biometric.blood': 'Blood Work',
  'biometric': 'Biometric',
  'sleep': 'Sleep',
  'mental_health.mood': 'Mood',
  'mental_health.stress': 'Stress',
  'mental_health.meditation': 'Meditation',
  'mental_health': 'Mental Health',
  'therapy.heat': 'Heat Therapy',
  'therapy.cold': 'Cold Therapy',
  'therapy': 'Therapy',
  'medical.symptom': 'Symptom',
  'medical.visit': 'Doctor Visit',
  'medical': 'Medical',
  'other': 'Other',
};

const TodayTimeline = ({ refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/health/entries', {
        params: { start_date: today, end_date: today, limit: 50 },
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching today entries:', error);
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries, refreshKey]);

  const getCategoryColor = (slug) => {
    const topCat = slug.split('.')[0];
    return CATEGORY_COLORS[topCat] || 'default';
  };

  const getCategoryLabel = (slug) => {
    return CATEGORY_LABELS[slug] || CATEGORY_LABELS[slug.split('.')[0]] || slug;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${m} ${ampm}`;
  };

  const formatValue = (entry) => {
    const parts = [];
    if (entry.value !== null && entry.value !== undefined) {
      parts.push(`${entry.value}${entry.value_unit ? ' ' + entry.value_unit : ''}`);
    }
    if (entry.calories) {
      parts.push(`${entry.calories} cal`);
    }
    if (entry.duration_minutes) {
      parts.push(`${entry.duration_minutes} min`);
    }
    return parts.join(' · ');
  };

  if (loading) {
    return (
      <div className="card-glow rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card overflow-hidden">
        <div className="px-6 py-4 border-b border-surface-border dark:border-surface-dark-border">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Today's entries</h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 pl-6">
                <div className="w-16 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Filter out fitbit-sourced entries (they're shown in summary cards)
  const userEntries = entries.filter(e => e.source !== 'fitbit');

  return (
    <div className="card-glow rounded-xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card overflow-hidden">
      <div className="px-6 py-4 border-b border-surface-border dark:border-surface-dark-border">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Today's entries</h2>
        {userEntries.length > 0 && (
          <p className="text-base text-gray-600 dark:text-gray-300">{userEntries.length} entr{userEntries.length === 1 ? 'y' : 'ies'} logged</p>
        )}
      </div>
      <div className="p-6">
        {userEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <svg className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No entries yet today</p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">Use the input above to log something</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-[7px] top-2 bottom-2 w-px bg-surface-border dark:bg-surface-dark-border" />

            <div className="space-y-4">
              {userEntries.map((entry) => (
                <div key={entry.id} className="relative pl-7">
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 border-primary-400 bg-primary-100 dark:bg-primary-900/50 dark:border-primary-500" />

                  {/* Line 1: Badge + entry type */}
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant={getCategoryColor(entry.category_slug)} className="shrink-0">
                      {getCategoryLabel(entry.category_slug)}
                    </Badge>
                    <span className="text-base font-semibold text-gray-900 dark:text-white truncate">
                      {(entry.entry_type || '').replace(/_/g, ' ')}
                    </span>
                  </div>

                  {/* Line 2: Time + values */}
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                    {entry.entry_time && (
                      <span className="font-mono">{formatTime(entry.entry_time)}</span>
                    )}
                    {entry.entry_time && formatValue(entry) && (
                      <span>·</span>
                    )}
                    {formatValue(entry) && (
                      <span>{formatValue(entry)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodayTimeline;
